import { useLayoutEffect, useRef, type ButtonHTMLAttributes, type ReactNode } from 'react'

export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger'
export type ButtonSize = 'sm' | 'md'

interface ButtonProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'className'> {
  variant?: ButtonVariant
  size?: ButtonSize
  full?: boolean
  /** Renders a spinner, disables the button, and marks it busy for AT. */
  busy?: boolean
  iconLeft?: ReactNode
  iconRight?: ReactNode
  className?: string
}

/**
 * The design system's Button, as CSS. See src/styles/components.css for why the
 * prototype's inline styles are not carried.
 *
 * `type="button"` is the default on purpose. HTML's default is `submit`, so a
 * bare <button> inside a form submits it -- which is how a "Show password"
 * toggle or a "Resend code" link posts the sign-in form instead.
 *
 * ── `busy` DISABLES, AND A DISABLED BUTTON LOSES FOCUS ──────────────────────
 *
 * `busy` sets the real `disabled` attribute and must keep doing so: it is the
 * re-entrancy protection, and it is what the tests assert (see the handoff's
 * trap 37 -- React flushes a discrete event synchronously, so a handler's own
 * guard is unreachable and `disabled` is the only thing that actually holds).
 *
 * The cost is that the browser blurs the element, and until Stage 7b nothing
 * put focus back. Measured on the deployed shape of the app: focus the sign-in
 * submit, press Enter, and once the request settles `document.activeElement` is
 * `<body>` -- so a keyboard user is returned to the top of the document with
 * the skip link to tab back from, on every sign-in, every upload, every journal
 * save, every approve and every reject. §7.2 asks for keyboard navigation and
 * that is not it.
 *
 * **The measurement is worth recording, and the FIRST lesson drawn from it was
 * wrong.** A bare `<button>` created in the console, focused, disabled and
 * re-enabled keeps `document.activeElement` across a microtask and a macrotask,
 * in Chromium and in WebKit -- which reads as "browsers do not blur on disable"
 * and nearly got this written off as refuted.
 *
 * The conclusion recorded here was "trust the screen, not the synthetic". That
 * is not it. A review pass re-ran the same synthetic probe and waited one
 * `requestAnimationFrame`: the blur fires there, universally, in both engines.
 * It is not that synthetic probes lie -- it is that **the blur is deferred to
 * the rendering lifecycle**, so any probe that stops at a macrotask is looking
 * one frame too early. Probe across a frame.
 *
 * That is also precisely what justifies `useLayoutEffect` below rather than
 * `useEffect`: the capture has to happen before the frame the blur rides on.
 *
 * So: remember whether this button held focus at the moment it went busy, and
 * take it back when it stops -- but ONLY if focus fell to `<body>`, so someone
 * who moved on keeps their place. `document.activeElement` is never null (it
 * falls back to `<body>`; trap 42), which is exactly what makes that the
 * signal.
 *
 * `useLayoutEffect`, not `useEffect`: the browser's blur is deferred, so a
 * passive effect can run after it and capture `<body>` as the thing that held
 * focus. A layout effect runs after the DOM mutation and before the paint that
 * carries the blur.
 *
 * A button that UNMOUNTS while busy is not covered and cannot be -- there is
 * nothing left to restore to. Those have their own deliberate targets; see
 * `LoadMore` and `RowActions`.
 */
export function Button({
  variant = 'primary',
  size = 'md',
  full = false,
  busy = false,
  iconLeft,
  iconRight,
  children,
  className,
  disabled,
  type = 'button',
  ...rest
}: ButtonProps) {
  const ref = useRef<HTMLButtonElement>(null)
  const heldFocus = useRef(false)

  useLayoutEffect(() => {
    const el = ref.current
    if (!el) return
    if (busy) {
      heldFocus.current = document.activeElement === el
      return
    }
    if (!heldFocus.current) return
    heldFocus.current = false
    if (document.activeElement === document.body) el.focus()
  }, [busy])

  const classes = [
    'btn',
    `btn--${variant}`,
    size === 'sm' && 'btn--sm',
    full && 'btn--full',
    className,
  ].filter(Boolean).join(' ')

  return (
    <button
      {...rest}
      ref={ref}
      type={type}
      className={classes}
      disabled={disabled || busy}
      aria-busy={busy || undefined}
    >
      {busy ? <span className="spinner" aria-hidden="true" /> : iconLeft}
      {children}
      {iconRight}
    </button>
  )
}
